import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations';
import useScrollAnimation from '../hooks/useScrollAnimation';

// Client Brand Partners (from NewTrustedBy in Next.js app)
import shalta from '../assets/partners/shalta.webp';
import sporta from '../assets/partners/sporta.webp';
import toy from '../assets/partners/toy.webp';
import verde from '../assets/partners/verde.webp';
import waad from '../assets/partners/waad.webp';
import ytLights from '../assets/partners/ytLights.webp';
import purely from '../assets/partners/purely.webp';
import pura from '../assets/partners/pura.webp';
import lab from '../assets/partners/lab.webp';
import fay from '../assets/partners/fay.webp';
import fancy from '../assets/partners/fancy.webp';
import elgarhy from '../assets/partners/elgarhy.webp';
import egysco from '../assets/partners/egysco.webp';
import dyna from '../assets/partners/dyna.webp';
import almasaudi from '../assets/partners/almasaudi.webp';
import almas from '../assets/partners/almas.webp';
import amody from '../assets/partners/3amody.webp';
import wind from '../assets/partners/wind.webp';
import pure from '../assets/partners/pure.webp';
import mt from '../assets/partners/mt-partner.webp';
import bexer from '../assets/partners/bexer.webp';
import arn from '../assets/partners/arn.webp';
import uniflm from '../assets/partners/uniflm.webp';
import petreeq from '../assets/partners/petreeq.webp';

const clientPartners = [
  { name: 'Shalta', src: shalta },
  { name: 'Sporta', src: sporta },
  { name: 'Toy', src: toy },
  { name: 'Verde', src: verde },
  { name: 'Waad', src: waad },
  { name: 'YT Lights', src: ytLights },
  { name: 'Purely', src: purely },
  { name: 'Pura', src: pura },
  { name: 'Lab', src: lab },
  { name: 'Fay', src: fay },
  { name: 'Fancy', src: fancy },
  { name: 'El Garhy', src: elgarhy },
  { name: 'Egysco', src: egysco },
  { name: 'Dyna', src: dyna },
  { name: 'Almasaudi', src: almasaudi },
  { name: 'Almas', src: almas },
  { name: 'Amody', src: amody },
  { name: 'Wind', src: wind },
  { name: 'Pure', src: pure },
  { name: 'MT', src: mt },
  { name: 'Bexer', src: bexer },
  { name: 'ARN', src: arn },
  { name: 'Uniflm', src: uniflm },
  { name: 'Petreeq', src: petreeq },
];

const CompactPartners = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isAr = language === 'ar';
  const [isTouchPaused, setIsTouchPaused] = useState(false);
  const observe = useScrollAnimation();

  // Duplicate array for continuous infinite marquee loop
  const marqueePartners = [...clientPartners, ...clientPartners];

  return (
    <section className="py-10 bg-surface-section border-b border-edge-light overflow-hidden relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-6">
        {/* Capsule Badge */}
        <div className="text-center mb-2">
          <span
            ref={observe}
            className="animate-fade-up inline-flex items-center justify-center px-6 py-2 rounded-full bg-badge text-primary-dark text-sm sm:text-base font-bold font-almarai mb-3"
          >
            {t.trustedBadge || (isAr ? 'شركاء النجاح' : 'Trusted By')}
          </span>
        </div>

        <h2
          ref={observe}
          className="animate-fade-up font-bold font-ibm text-heading text-xl sm:text-2xl md:text-3xl mb-3"
          style={{ transitionDelay: '0.1s' }}
        >
          {t.trustedTitle}
        </h2>
        <p
          ref={observe}
          className="animate-fade-up font-almarai text-body-muted text-sm sm:text-base max-w-xl mx-auto leading-relaxed"
          style={{ transitionDelay: '0.2s' }}
        >
          {t.trustedSubtext}
        </p>
      </div>

      {/* Marquee Container with subtle gradient masks */}
      <div
        dir="ltr"
        className="relative w-full overflow-hidden py-3 touch-pan-y"
        onTouchStart={() => setIsTouchPaused(true)}
        onTouchEnd={() => setIsTouchPaused(false)}
        onTouchCancel={() => setIsTouchPaused(false)}
      >
        <div className="absolute inset-y-0 left-0 w-16 sm:w-28 bg-gradient-to-r from-surface-section via-surface-section/80 to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-16 sm:w-28 bg-gradient-to-l from-surface-section via-surface-section/80 to-transparent z-10 pointer-events-none" />

        <div
          dir="ltr"
          className={isAr ? 'animate-marquee-track-rtl' : 'animate-marquee-track-ltr'}
          style={{ animationPlayState: isTouchPaused ? 'paused' : 'running' }}
        >
          {marqueePartners.map((item, idx) => (
            <div key={idx} className="px-2 sm:px-3">
              <div
                className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full bg-white border border-edge-light shadow-card-soft hover:shadow-md hover:scale-108 transition-all duration-300 flex items-center justify-center p-2.5 sm:p-3 md:p-3.5 shrink-0 group/circle"
                title={item.name}
              >
                <img
                  src={item.src}
                  alt={`${item.name} logo`}
                  loading="lazy"
                  className="w-full h-full object-contain transition-transform duration-300 group-hover/circle:scale-110"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CompactPartners;
